import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout } from 'tea-component';
import {
  connect,
  GUIDE_TYPE_OPINION_OVERVIEW,
  useDispatch,
  useIntl,
} from 'umi';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import { compareFilterChange } from '../KeyOpinions/utils';
import ContentBody from '@op/components/ContentBody';
import CardLoading from '@op/components/CardLoading';
import MentionsSentiment from './MentionsSentiment';
import GameStore from './GameStore';
import Community from './Community';
import PR from './PR';
import { PAGE_NAME } from '@op/constants/page';
import { distinguishOfficialAccounts } from '@op/components/FilterCommon/utils/monitorUtil';
import {
  buriedBtnPoint,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import { storage } from '@/utils/utils';
import './index.scss';
import { arrayToObj } from '@op/utils';
import { chartAllColors } from '@op/constants';
import keyMetrics from '@op/operationLog/pages/keyMetrics';
import DownloadDialog from '@op/components/DownloadCommon/download-dialog';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import UrlButton from '@op/pages/Performance/components/UrlButton';
import { useDashboardGamePermissions } from '@op/pages/Performance/hooks/useGamePermissions';
import { getOtherSystemUrl } from '@op/utils/url';
import { intelligenceMapTips } from '@op/components/BubbleCommon';
import { POE2 } from '@op/constants/specialGame';
import CardCommon from '@op/components/CardCommon';
import { OPINION_GUIDE_BOX_LIST1 } from '@home/components/GuideModule/GuideOpinionBox';
import { opinionRankShow } from '@op/utils/storageUtils';
import { DBO_PAGE } from '@op/utils/format';
import { StyleSpecial } from '@op/constants/common';
import useHasMenu from '@op/hooks/useHasMenu';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';

const { Content } = Layout;
function CompareKeyMetrics({ gameInfo, wholeGameInfo, userInfo }: any) {
  const { formatMessage } = useIntl();
  const { downloadLegacy, downloading } = useDownloadParams();
  const [officialAccount, setOfficialAccount] = useState<Array<string>>([]);
  const [competitorGames, setCompetitorGames] = useState({});
  const { Competitor_Rank_Hide } = useGetSingePageSourceRule({
    keys: ['Competitor_Rank_Hide'],
  });
  const storageRankShow = opinionRankShow(userInfo);
  const [showRank, setShowRank] = useState(
    storageRankShow.get(Competitor_Rank_Hide),
  );
  const [downloadVisible, setDownloadVisible] = useState(false);
  const [games, setGames] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [hasSummaryData, setHasSummaryData] = useState(false);
  const contentRef = useRef(null);
  const filterCondInfo = useRef({});
  const isPOE2 = POE2.includes(wholeGameInfo?.edition_unified_id);
  const {
    scrollBody,
    loading,
    filter,
    changeFilter,
    filterOptions,
    renderExcludeFilterMemo,
  } = useFilterDataHook({
    gameInfo,
    pageName: 'Key_Metrics',
    renderExcludeFilter: ['competitorGames'],
  });
  useInitBuriedPointInfo({ buriedPoint: keyMetrics });

  const {
    dashboardUrl,
    showDashboardBtn,
    dashboardBtnDisabled,
    dashboardTipsMap,
  } = useDashboardGamePermissions({
    gameInfo,
  });

  const dispatch = useDispatch();
  useEffect(() => {
    const list = storage.getLocalStorageItem(OPINION_GUIDE_BOX_LIST1) || [];
    if (userInfo?.openid && !list.includes(userInfo.openid)) {
      dispatch({
        type: 'guide/save',
        payload: {
          guideSys: '',
          guideType: GUIDE_TYPE_OPINION_OVERVIEW,
        },
      });
    }
  }, [userInfo]);

  useEffect(() => {
    if (scrollBody?.current && !loading) {
      if (contentRef?.current) {
        DBO_PAGE.scroll('KeyMetricsContentId');
        setTimeout(() => {
          setScrolled(true);
        }, 500);
      }
    }
  }, [loading]);

  const { hasMenu } = useHasMenu('Steam Performance', { gameType: 'pc' });

  const showSummaryEvents = useMemo(() => {
    const flag = compareFilterChange(filter, filterCondInfo.current);
    setHasSummaryData(false);
    console.log('是请求例行报告: ', flag);
    return flag;
  }, [filter]);

  return (
    <Content className="dbo-page dbo-page-metrics" ref={scrollBody}>
      <ContentBody
        pageName={PAGE_NAME.KEY_METRICS}
        GotoFeeds
        contentHeard={
          <Content.Header
            className="dbo-header"
            title={formatMessage({ id: 'Key Metrics-1' })}
            subtitle={
              isPOE2 ? (
                <div className="dbo-page-header-subtitle">
                  {formatMessage({
                    id: 'The data from each channel is currently updated hourly',
                  })}
                </div>
              ) : undefined
            }
            operation={
              <>
                <UrlButton
                  url={dashboardUrl}
                  showBtn={showDashboardBtn}
                  buttonText="Dashboard"
                  disabled={dashboardBtnDisabled}
                  bubbleContent={dashboardTipsMap}
                ></UrlButton>
                <UrlButton
                  url={getOtherSystemUrl(wholeGameInfo)}
                  buttonText="Intelligence"
                  bubbleContent={intelligenceMapTips}
                ></UrlButton>
              </>
            }
          ></Content.Header>
        }
      >
        <FilterFixed
          filter={[...filterOptions, 'competitorGames']}
          scrollBody={scrollBody}
          renderExcludeFilter={renderExcludeFilterMemo}
          // styles={{ visibility: loading ? 'hidden' : 'visible' }}
          hideTop={loading ? 150 : 200}
          backCondOriginData={(data) => {
            filterCondInfo.current = data;
            const { monitor_by_account_category, competitor_games } =
              data || {};
            const account = distinguishOfficialAccounts(
              monitor_by_account_category?.monitory || {},
            );
            const localGameList = competitor_games || [];
            localGameList.unshift({
              ...wholeGameInfo,
              color: chartAllColors[0],
            });
            setGames(
              localGameList
                .map(
                  ({
                    edition_unified_id,
                    entity_type,
                    id_type,
                    release_time,
                  }: any) => {
                    if (!edition_unified_id) return null;
                    return {
                      edition_unified_id,
                      entity_type,
                      id_type,
                      release_time,
                    };
                  },
                )
                .filter(Boolean),
            );
            setCompetitorGames(arrayToObj(localGameList, 'edition_unified_id'));
            setOfficialAccount(account['official-accounts']);
          }}
          confirm={(value: any) => {
            changeFilter({ ...value, ...gameInfo });
          }}
        ></FilterFixed>
        <CardLoading loading={loading}>
          <div
            ref={contentRef}
            id="KeyMetricsContentId"
            style={{ [StyleSpecial.SCROLL]: 50 }}
          >
            <MentionsSentiment
              filter={filter}
              gameInfo={gameInfo}
              games={games}
              wholeGameInfo={wholeGameInfo}
              gameData={competitorGames}
              showRank={showRank}
              setDownloadVisible={setDownloadVisible}
              hideCompetitorRank={Competitor_Rank_Hide}
              showSummaryEvents={showSummaryEvents}
              summaryHasData={hasSummaryData}
              onSummaryHasDataChange={setHasSummaryData}
              onChange={(value: any) => {
                buriedBtnPoint('KM_ShowCompetitor', value.showRank);
                setShowRank(value.showRank);
                storageRankShow.set(value.showRank);
              }}
            ></MentionsSentiment>
            {hasMenu && (
              <GameStore
                filter={filter}
                gameInfo={gameInfo}
                wholeGameInfo={wholeGameInfo}
                games={games}
                gameData={competitorGames}
                showRank={showRank}
              ></GameStore>
            )}
            <Community
              filter={filter}
              gameInfo={gameInfo}
              officialAccount={officialAccount}
              games={games}
              gameData={competitorGames}
              showRank={showRank}
            ></Community>
            <PR
              filter={filter}
              wholeGameInfo={wholeGameInfo}
              gameInfo={gameInfo}
              games={games}
              gameData={competitorGames}
              showRank={showRank}
            ></PR>
          </div>
        </CardLoading>
        <>
          {!scrolled && (
            <CardCommon
              className="dbo-key-metrics-whole-screen-loading"
              loading={true}
            ></CardCommon>
          )}
        </>
      </ContentBody>
      <DownloadDialog
        visible={downloadVisible}
        setVisible={setDownloadVisible}
        confirmLoading={downloading}
        download={(params: any) => {
          console.log('download metrics', params);
          const { accumulation, metrics } = params;
          const group_bys: Array<string> = [];
          const keyMap: any = {
            by_channel: 'channel',
            by_country: 'country',
            by_date: 'time',
            by_language: 'language',
            by_sentiment: 'sentiment',
            // by_net_sentiment: 'net_sentiment',
            by_rating: 'rating',
          };

          Object.keys(accumulation).forEach((key) => {
            const element = accumulation[key];
            if (element) {
              group_bys.push(keyMap[key]);
            }
          });
          const metricsMap: any = {
            Mentions: 'mentions',
            'Account Post': 'account_posts',
            Engagement: 'engagement',
            Sentiment: 'avg_sentiment',
            'Brand Health': 'brand_health',
            'Non Negative%': 'non_negative_ratio',
            'User Score': 'user_score',
          };

          downloadLegacy({
            simpleName: metrics?.join('_'),
            sheetName: 'Key Metrics',
            align: 'center',
            params: {
              ...filter,
              ...gameInfo,
              metrics: metrics.map((item: string | number) => metricsMap[item]),
              group_bys,
            },
            downloadFunc: 'downloadKeyMetrics',
          });
        }}
        gameInfo={gameInfo}
      ></DownloadDialog>
    </Content>
  );
}

export default connect((state: any) => {
  const { opinionCommon, opinionFilter, home } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    filter: opinionFilter,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    userInfo: home.userInfo,
  };
})(CompareKeyMetrics);
